import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, CheckCircle2, EyeOff, FileText, LifeBuoy } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function SupportDesk() {
  const [tab, setTab] = useState("requests");
  const [requests, setRequests] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, d] = await Promise.all([
        api.get("/staff/support/requests"),
        api.get("/staff/support/documents"),
      ]);
      setRequests(r.data.requests || []);
      setDocuments(d.data.documents || []);
    } catch (error) {
      toast.error(formatApiError(error, "Could not load the support desk"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const patchDocument = async (document, changes) => {
    setBusy(document.id);
    try {
      const { data } = await api.patch(`/staff/support/documents/${document.id}`, changes);
      setDocuments((prev) => prev.map((item) => (item.id === data.id ? data : item)));
    } catch (error) {
      toast.error(formatApiError(error, "Could not update the document"));
    } finally {
      setBusy(null);
    }
  };

  const upload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy("upload");
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/staff/support/documents", body, { headers: { "Content-Type": "multipart/form-data" } });
      setDocuments((prev) => [data, ...prev]);
      toast.success(`${data.title} uploaded as unapproved — tag it and approve it to make it searchable`);
    } catch (error) {
      toast.error(formatApiError(error, "Upload failed"));
    } finally {
      setBusy(null);
      event.target.value = "";
    }
  };

  const approvedCount = documents.filter((d) => d.approved && !d.withdrawn).length;

  return (
    <div data-testid="support-desk">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-ei-navy">Technical support desk</h1>
          <p className="text-sm text-ei-graphite/70 mt-1">
            Customer requests from the AI assistant, and the documents it is allowed to quote.
          </p>
        </div>
        <div className="text-xs text-ei-graphite/60 tnum" data-testid="support-desk-counts">
          {requests.length} requests · {approvedCount} searchable documents
        </div>
      </div>

      <div className="mt-5 flex gap-2" role="tablist">
        {[["requests", "Support requests", LifeBuoy], ["documents", "Documents", FileText]].map(([key, label, Icon]) => (
          <button
            key={key}
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            data-testid={`support-tab-${key}`}
            className={`inline-flex items-center gap-2 font-display text-xs font-bold px-4 py-2.5 rounded-sm border min-h-[40px] transition-colors ${
              tab === key ? "bg-ei-navy text-ei-white border-ei-navy" : "bg-white text-ei-navy border-ei-mist hover:border-ei-gold"
            }`}
          >
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-ei-navy/40" /></div>
      ) : tab === "requests" ? (
        <div className="mt-5 space-y-4" data-testid="support-requests-list">
          {requests.length === 0 ? (
            <div className="bg-white border border-ei-mist rounded-md p-8 text-center text-sm text-ei-graphite/60">
              No technical support requests yet.
            </div>
          ) : requests.map((request) => (
            <div key={request.id} className="bg-white border border-ei-mist rounded-md p-5" data-testid={`support-request-${request.reference}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-display text-sm font-bold text-ei-navy">{request.reference} · {request.product}</div>
                  <div className="text-xs text-ei-graphite/60 mt-0.5">
                    {request.created_at?.slice(0, 16).replace("T", " ")}
                    {request.fault_code ? ` · fault code ${request.fault_code}` : ""}
                  </div>
                </div>
                <span className={`text-[11px] font-display font-bold px-2.5 py-1 rounded-sm ${
                  request.email_status === "sent" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"
                }`} data-testid={`support-request-email-${request.reference}`}>
                  {request.email_status === "sent" ? "Emailed" : `Email: ${request.email_status || "not sent"}`}
                </span>
              </div>
              <p className="mt-3 text-sm text-ei-graphite whitespace-pre-wrap">{request.summary}</p>
              {request.attempted && (
                <p className="mt-2 text-sm text-ei-graphite/80"><span className="text-xs uppercase tracking-wider text-ei-graphite/50">Already tried: </span>{request.attempted}</p>
              )}
              <div className="mt-3 text-xs text-ei-graphite/70">
                {request.contact_name || "No name"} · {request.contact_email || "no email"} · {request.contact_phone || "no phone"}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-5" data-testid="support-documents-list">
          <label className="inline-flex items-center gap-2 font-display text-xs font-bold text-ei-navy border border-ei-mist px-4 py-3 rounded-sm hover:bg-ei-navy/5 cursor-pointer min-h-[44px]" data-testid="support-upload-label">
            {busy === "upload" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload a PDF document
            <input type="file" accept="application/pdf" className="hidden" onChange={upload} data-testid="support-upload-input" />
          </label>
          <p className="text-xs text-ei-graphite/60 mt-2">Uploads start unapproved. Tag the models, set the revision date, then approve to make it searchable.</p>

          <div className="mt-4 bg-white border border-ei-mist rounded-md overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-ei-navy/[0.04] text-left">
                <tr>
                  {["Document", "Models", "Revision", "Pages", "Status", ""].map((heading) => (
                    <th key={heading} className="px-4 py-3 text-[11px] uppercase tracking-wider text-ei-graphite/60 font-display">{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {documents.map((document) => (
                  <tr key={document.id} className="border-t border-ei-mist" data-testid={`support-document-${document.id}`}>
                    <td className="px-4 py-3">
                      <a href={document.url} target="_blank" rel="noreferrer" className="text-ei-navy hover:text-ei-gold underline underline-offset-2">{document.title}</a>
                      <div className="text-[11px] text-ei-graphite/50">{document.brand || "Brand not tagged"} · {document.source}</div>
                    </td>
                    <td className="px-4 py-3">
                      <input
                        defaultValue={(document.model_tags || []).join(", ")}
                        onBlur={(event) => {
                          const next = event.target.value.split(",").map((value) => value.trim()).filter(Boolean);
                          if (next.join(",") !== (document.model_tags || []).join(",")) patchDocument(document, { model_tags: next });
                        }}
                        className="w-48 border border-ei-mist rounded-sm px-2 py-1.5 text-xs focus:outline-none focus:border-ei-navy"
                        data-testid={`support-document-tags-${document.id}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        defaultValue={document.revision_date || ""}
                        placeholder="2026-04"
                        onBlur={(event) => {
                          if (event.target.value !== (document.revision_date || "")) patchDocument(document, { revision_date: event.target.value });
                        }}
                        className="w-24 border border-ei-mist rounded-sm px-2 py-1.5 text-xs focus:outline-none focus:border-ei-navy"
                        data-testid={`support-document-revision-${document.id}`}
                      />
                    </td>
                    <td className="px-4 py-3 tnum text-ei-graphite/70">{document.pages}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] font-display font-bold px-2 py-1 rounded-sm ${
                        document.withdrawn ? "bg-red-50 text-red-700" : document.approved ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"
                      }`}>
                        {document.withdrawn ? "Withdrawn" : document.approved ? "Searchable" : "Awaiting approval"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => patchDocument(document, { approved: !document.approved, withdrawn: false })}
                          disabled={busy === document.id}
                          className="inline-flex items-center gap-1.5 text-[11px] font-display font-bold text-ei-navy border border-ei-mist px-2.5 py-2 rounded-sm hover:bg-ei-navy/5 disabled:opacity-40"
                          data-testid={`support-document-approve-${document.id}`}
                        >
                          <CheckCircle2 className="w-3 h-3" /> {document.approved ? "Unapprove" : "Approve"}
                        </button>
                        <button
                          onClick={() => patchDocument(document, { withdrawn: !document.withdrawn })}
                          disabled={busy === document.id}
                          className="inline-flex items-center gap-1.5 text-[11px] font-display font-bold text-red-700 border border-red-200 px-2.5 py-2 rounded-sm hover:bg-red-50 disabled:opacity-40"
                          data-testid={`support-document-withdraw-${document.id}`}
                        >
                          <EyeOff className="w-3 h-3" /> {document.withdrawn ? "Restore" : "Withdraw"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
